import { findStrayDevices } from "../KarlGroupingPhase/strayDevices";
import { readRouteWhy } from "./routeWhy";
import { readCircuitKey } from "../circuitKeys";
import type { CablePosition } from "../findCableGaps";
import type { KarlAssignment, KarlCircuit } from "../karlSteps";
import type { StrayGroup } from "../KarlGroupingPhase/strayDevices";
import type {
  BuildRouteWalk,
  RouteFlag,
  RouteWalkItem,
  RouteWhy,
  RouteWhyKind,
} from "./types";

/**
 * The run writes this in front of a group it bucketed by symbol because it read
 * no identifier beside the devices. "grp·Sirene" is not a printed key.
 */
const placedPositions = (circuit: KarlCircuit): CablePosition[] =>
  circuit.devices
    .map((device) => device.position)
    .filter((position): position is CablePosition => position !== null);

const flagsFor = (
  circuit: KarlCircuit,
  stray: StrayGroup | undefined,
): RouteFlag[] => {
  const flags: RouteFlag[] = [];
  if (stray) {
    flags.push({
      kind: "stray_devices",
      devices: stray.strays.length,
      positions: stray.strays.map((device) => device.position),
      destinationIds: stray.strays.map((device) => device.destinationId),
    });
  }
  if (!readCircuitKey({ circuit })) {
    flags.push({
      kind: "unkeyed_bag",
      devices: circuit.devices.length,
      positions: placedPositions(circuit),
      // A bag has no per-device identity to act on — it is the whole circuit.
      destinationIds: [],
    });
  }
  if (!circuit.devices.length) {
    flags.push({ kind: "no_devices", devices: 0, positions: [], destinationIds: [] });
  }
  return flags;
};

/** The nameless bucket is a device with no feed point — a different conversation. */
const isOrphanBucket = (assignment: KarlAssignment): boolean =>
  assignment.sourceName === "";

/**
 * How sure the run is that these devices belong together, strongest first —
 * the same four kinds `routeWhy` grades, in the same order. He walks the runs
 * we did a good job on first and the doubtful ones once he is warmed up:
 * *"for the first we should show the confident ones, the ones that got id and
 * we did a good job"*.
 *
 * `readCircuitKey` alone would only split keyed from unkeyed; a key nobody can
 * corroborate is not the same evidence as one printed beside the devices, and
 * the difference is exactly what he is being asked to check.
 */
const CONFIDENCE_ORDER: RouteWhyKind[] = [
  "key_on_plan",
  "key_from_schematic",
  "board_only",
  "grouped_by_symbol",
];

const confidenceRank = (why: RouteWhy): number => {
  const rank = CONFIDENCE_ORDER.indexOf(why.kind);
  return rank === -1 ? CONFIDENCE_ORDER.length : rank;
};

interface RankedRoute {
  item: RouteWalkItem;
  /** Boards keep the rank the run published them in. */
  boardRank: number;
  isOrphan: boolean;
}

/**
 * Every tie is broken, down to the circuit id, so the walk cannot depend on
 * whether the engine's sort happens to be stable: "route 12 of 83" is a number
 * he reads out loud, and it must be the same number on the next render.
 */
const byConfidenceThenBoard = (a: RankedRoute, b: RankedRoute): number =>
  confidenceRank(a.item.why) - confidenceRank(b.item.why) ||
  Number(a.isOrphan) - Number(b.isOrphan) ||
  a.boardRank - b.boardRank ||
  a.item.label.localeCompare(b.item.label) ||
  a.item.circuitId.localeCompare(b.item.circuitId);

export const buildRouteWalk: BuildRouteWalk = ({ steps }) => {
  const strays = new Map<string, StrayGroup>(
    findStrayDevices({ assignments: steps.assignments }).map((group) => [
      group.circuitId,
      group,
    ]),
  );

  const ranked: RankedRoute[] = steps.assignments.flatMap((assignment, rank) =>
    assignment.circuits.map((circuit) => ({
      item: {
        circuitId: circuit.circuitId,
        label: readCircuitKey({ circuit }) ?? circuit.circuitId,
        board: assignment.sourceName,
        deviceCount: circuit.devices.length,
        lengthM: circuit.lengthM,
        cableType: circuit.cableType,
        why: readRouteWhy({ steps, circuitId: circuit.circuitId }),
        positions: placedPositions(circuit),
        flags: flagsFor(circuit, strays.get(circuit.circuitId)),
      },
      boardRank: rank,
      isOrphan: isOrphanBucket(assignment),
    })),
  );

  const routes = ranked
    .sort(byConfidenceThenBoard)
    .map((entry) => entry.item);

  return { routes, total: routes.length };
};
